import React, {forwardRef, useEffect, useImperativeHandle} from 'react';
import {batch, connect} from 'react-redux';
import PropTypes from 'prop-types';
import {bindActionCreators} from 'redux';

import {isV2Instrument} from 'stores/business/instrumentStatusBusiness';
import withIntl from '../../../lib/withIntl';
import '../SetupWizard.scss';
import SetupWizardGeneralStepPage from './SetupWizardGeneralStepPage';
import {
  handleCalibrateVision,
  showHeadCoverConfirmationModal,
} from '../../../stores/business/setupWizardBusiness/setupWizardBusiness';
import {isSlantedDetectionSkipped, preMoveCalibrateScanStuckTips} from '../../../stores/business/instrumentBusiness';
import {hideLoading, showLoading} from '../../../stores/actions/appStateAction';
import {clearWizardFlag} from '../../../stores/actions/wizardStateAction';
import {initializeDefaultStepData} from '../../../stores/business/setupWizardBusiness/commonSetupWizardBusiness';
import {
  handleDecrementCurrentWizardView,
  handleIncrementCurrentWizardView,
} from '../../../stores/business/setupWizardBusiness/setupWizardNavigationBusiness';
import {saveState} from '../../../stores/business/stateBusiness';
import {VisionCalibrationSubstep, WizardFlag} from '../../../constants/SetupWizardEnum';
import {handleEjectTray} from '../../../stores/business/setupWizardBusiness/setupWizardInstrumentBusiness';
import FormattedHTMLMessage from '../../../components/FormattedHTMLMessage';
import VisionCalibrationV2SetupWizard from './FastV2/VisionCalibrationV2SetupWizard';

const mapStateToProps = (state) => ({
  // wizardViewDataState: state.wizardState.wizardViewData,
  wizardFlagState: state.wizardState.wizardFlag,
});

const mapDispatchToProps = (dispatch) => ({
  setupWizardBusinessCalibrateVision: bindActionCreators(handleCalibrateVision, dispatch),
  SetupWizardBusinessShowHeadCoverConfirmationModal: bindActionCreators(showHeadCoverConfirmationModal, dispatch),
  commonSWBusinessInitializeDefaultStepData: bindActionCreators(initializeDefaultStepData, dispatch),
  instrumentBusinessIsSlantedDetectionSkipped: bindActionCreators(isSlantedDetectionSkipped, dispatch),
  instrumentBusinessPreMoveCalibrateScanStuckTips: bindActionCreators(preMoveCalibrateScanStuckTips, dispatch),
  SWNavigationBusinessHandleIncrementCurrentWizardView: bindActionCreators(handleIncrementCurrentWizardView, dispatch),
  SWNavigationBusinessHandleDecrementCurrentWizardView: bindActionCreators(handleDecrementCurrentWizardView, dispatch),
  SWInstrumentBusinessEjectTray: bindActionCreators(handleEjectTray, dispatch),
  wizardStateActionClearWizardFlag: bindActionCreators(clearWizardFlag, dispatch),
  stateBusinessSaveState: bindActionCreators(saveState, dispatch),
  appStateActionShowLoading: bindActionCreators(showLoading, dispatch),
  appStateActionHideLoading: bindActionCreators(hideLoading, dispatch),
});

const StepFiveSetupWizard = forwardRef(
  (
    {
      setupWizardBusinessCalibrateVision,
      SetupWizardBusinessShowHeadCoverConfirmationModal,
      commonSWBusinessInitializeDefaultStepData,
      instrumentBusinessIsSlantedDetectionSkipped,
      instrumentBusinessPreMoveCalibrateScanStuckTips,
      SWNavigationBusinessHandleIncrementCurrentWizardView,
      SWNavigationBusinessHandleDecrementCurrentWizardView,
      // SWInstrumentBusinessEjectTray,
      wizardStateActionClearWizardFlag,
      stateBusinessSaveState,
      appStateActionShowLoading,
      appStateActionHideLoading,
      onSaveStepData,
      stepData,
      wizardFlagState,
    },
    ref,
  ) => {
    const {currentSubstep, processedSubstep, hasScanned} = stepData;
    const onButtonClick = async () => setupWizardBusinessCalibrateVision(currentSubstep);

    const doPreMoveScanStuckTips = async () => {
      appStateActionShowLoading();
      await instrumentBusinessPreMoveCalibrateScanStuckTips();
      appStateActionHideLoading();
    };

    const onContinueScanTips = async () => {
      const shouldSkipSlantedDetection = await instrumentBusinessIsSlantedDetectionSkipped();
      if (shouldSkipSlantedDetection) {
        if (!isV2Instrument()) {
          await doPreMoveScanStuckTips();
        }

        onSaveStepData({
          currentSubstep: VisionCalibrationSubstep.SLANTED_TIPS,
          isSlantedDetectionSkipped: shouldSkipSlantedDetection,
          processedSubstep: processedSubstep === 0 ? VisionCalibrationSubstep.SLANTED_TIPS : processedSubstep,
        });
      }

      wizardStateActionClearWizardFlag();
    };

    const onContinueSlantedTips = async () => {
      if (!isV2Instrument()) {
        await doPreMoveScanStuckTips();
      }
    };

    const onContinueStuckTips = async () => {
      const onContinueCallback = async () => {
        if (currentSubstep === VisionCalibrationSubstep.STUCK_TIPS_GUIDE) {
          onSaveStepData({
            currentSubstep: VisionCalibrationSubstep.STUCK_TIPS_RESULT,
            processedSubstep: VisionCalibrationSubstep.STUCK_TIPS_GUIDE,
          });
        }

        SWNavigationBusinessHandleIncrementCurrentWizardView(false, true);
        await stateBusinessSaveState();
      };

      if (isV2Instrument()) onContinueCallback();
      else SetupWizardBusinessShowHeadCoverConfirmationModal(true, onContinueCallback);

      return {isChangeStep: false, isCheckContinueButtonAccess: false};
    };

    const onContinue = async () => {
      switch (currentSubstep) {
        case VisionCalibrationSubstep.SCAN_TIPS:
          return onContinueScanTips();
        case VisionCalibrationSubstep.SLANTED_TIPS:
          return onContinueSlantedTips();
        case VisionCalibrationSubstep.STUCK_TIPS_GUIDE:
        case VisionCalibrationSubstep.STUCK_TIPS_RESULT:
          return onContinueStuckTips();
        default:
          return {};
      }
    };

    const onBackStuckTipsGuide = async () => {
      const onBackCallback = async () => {
        const shouldSkipSlantedDetection = await instrumentBusinessIsSlantedDetectionSkipped();

        batch(async () => {
          if (shouldSkipSlantedDetection) {
            onSaveStepData({
              currentSubstep: VisionCalibrationSubstep.SLANTED_TIPS,
              isSlantedDetectionSkipped: shouldSkipSlantedDetection,
            });
          }

          SWNavigationBusinessHandleDecrementCurrentWizardView(true);
        });

        await stateBusinessSaveState();
      };

      if (isV2Instrument()) onBackCallback();
      else SetupWizardBusinessShowHeadCoverConfirmationModal(true, onBackCallback);

      return {isChangeStep: false, isCheckContinueButtonAccess: false};
    };

    const onBackStuckTipsResult = () => {
      onSaveStepData({currentSubstep: VisionCalibrationSubstep.STUCK_TIPS_GUIDE});
    };

    const onBack = async () => {
      // commonSWBusinessInitializeDefaultStepData(null, false);

      switch (currentSubstep) {
        case VisionCalibrationSubstep.STUCK_TIPS_GUIDE:
          return onBackStuckTipsGuide();
        case VisionCalibrationSubstep.STUCK_TIPS_RESULT:
          return onBackStuckTipsResult();
        default:
          return {};
      }
    };

    useImperativeHandle(ref, () => ({
      onContinue,
      onBack,
    }));

    useEffect(() => {
      if (wizardFlagState === WizardFlag.NONE) {
        commonSWBusinessInitializeDefaultStepData(null, false);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSubstep, wizardFlagState]);

    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {isV2Instrument() && currentSubstep === VisionCalibrationSubstep.STUCK_TIPS_GUIDE ? (
          <VisionCalibrationV2SetupWizard.StuckTipsDetection onCalibrate={onButtonClick} />
        ) : (
          <SetupWizardGeneralStepPage
            withTitleAndBodyText={!(hasScanned || currentSubstep === VisionCalibrationSubstep.STUCK_TIPS_RESULT)}
            withImageCaption={
              !(
                (!hasScanned && currentSubstep === VisionCalibrationSubstep.STUCK_TIPS_RESULT) ||
                currentSubstep === VisionCalibrationSubstep.STUCK_TIPS_GUIDE
              )
            }
            titleIntlId="setupWizard.step.visionCalibration.title"
            onButtonClick={onButtonClick}
            buttonProps={{
              label: <FormattedHTMLMessage id="setupWizard.step.visionCalibration.firstButton" />,
            }}
          />
        )}
      </>
    );
  },
);

StepFiveSetupWizard.propTypes = {
  setupWizardBusinessCalibrateVision: PropTypes.func.isRequired,
  SetupWizardBusinessShowHeadCoverConfirmationModal: PropTypes.func.isRequired,
  commonSWBusinessInitializeDefaultStepData: PropTypes.func.isRequired,
  instrumentBusinessIsSlantedDetectionSkipped: PropTypes.func.isRequired,
  instrumentBusinessPreMoveCalibrateScanStuckTips: PropTypes.func.isRequired,
  SWNavigationBusinessHandleIncrementCurrentWizardView: PropTypes.func.isRequired,
  SWNavigationBusinessHandleDecrementCurrentWizardView: PropTypes.func.isRequired,
  // SWInstrumentBusinessEjectTray: PropTypes.func.isRequired,
  wizardStateActionClearWizardFlag: PropTypes.func.isRequired,
  stateBusinessSaveState: PropTypes.func.isRequired,
  appStateActionShowLoading: PropTypes.func.isRequired,
  appStateActionHideLoading: PropTypes.func.isRequired,
  onSaveStepData: PropTypes.func.isRequired,
  stepData: PropTypes.object.isRequired,
  wizardFlagState: PropTypes.string.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(withIntl(StepFiveSetupWizard));
